import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Clipboard,
  Calendar,
  Database,
  Activity,
  AlertTriangle,
  Info,
  ArrowUp,
  ArrowDown,
  Brain,
  Users,
  Heart,
  BookOpen,
} from "lucide-react";
import { calculateOverviewMetrics, normalizeMetric } from "./rcCalculations";

/* Evidence level indicator colors */
const evidenceColor = {
  high: "bg-emerald-400",
  moderate: "bg-amber-400",
  low: "bg-slate-500",
};

const EvidenceDot = ({ level }) => (
  <span
    className={`inline-block w-2 h-2 rounded-full ${
      evidenceColor[level] || evidenceColor.low
    } mt-1`}
    title={level ? `${level} confidence` : ""}
  />
);

/* one row inside the Risk Indicators card */
const RiskRow = ({ label, metric, showSampleSizes }) => {
  if (!metric) {
    return (
      <div className="flex items-start gap-3">
        <EvidenceDot level="low" />
        <div>
          <div className="text-white font-medium">{label}</div>
          <div className="text-slate-400 text-sm">Insufficient data</div>
        </div>
      </div>
    );
  }

  // This determines if it's a protective factor (OR < 1 means protective)
  const protective = metric.or < 1;

  return (
    <div className="flex items-start gap-3">
      <EvidenceDot level={metric.strength} />
      <div className="w-full">
        <div className="flex items-center justify-between">
          <span className="text-white font-medium">{label}</span>
          {/* Display warning for small samples */}
          {metric.smallSample && showSampleSizes && (
            <span className="text-amber-400 text-xs flex items-center">
              <AlertTriangle size={12} className="mr-1" />
              Limited data
            </span>
          )}
        </div>
        <div className="text-slate-400 text-sm">
          {metric.rateExp}% vs {metric.rateRef}% · {metric.or.toFixed(1)}×{" "}
          {protective ? "lower" : "higher"} relapse odds
          {/* Display sample sizes when available */}
          {showSampleSizes && metric.sampleSizes && (
            <span className="block text-xs text-slate-500 mt-1">
              Sample: n={metric.sampleSizes.with} with factor, n=
              {metric.sampleSizes.without} without
              {metric.smallSample && (
                <span className="ml-1 font-medium text-amber-400">
                  (interpret with caution)
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* one‑liner tooltip used in card headers */
const Hint = ({ text }) => (
  <span className="relative inline-block group">
    <Info size={16} className="text-slate-400 cursor-pointer" />

    {/* bubble */}
    <span
      /* centre‑align, 8 px below the icon */
      className="pointer-events-none absolute left-1/2 top-full mt-2
                 w-60 -translate-x-1/2 rounded-md bg-slate-800/95 p-2
                 text-xs leading-snug text-slate-200 shadow-lg
                 opacity-0 group-hover:opacity-100 transition-opacity
                 backdrop-blur-sm z-50"
    >
      {text}
    </span>
  </span>
);

const RCPublicOverview = ({ organizationId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rcData, setRcData] = useState(null);
  const [domainData, setDomainData] = useState([]);
  const [domainDistributions, setDomainDistributions] = useState([]);
  const [riskData, setRiskData] = useState({
    traumaOddsRatio: null,
    housingOddsRatio: null,
    purposeRelapseReduction: null,
    twoParentAdvantage: null,
    kidsRelationshipAdvantage: null,
    coOccurringOddsRatio: null,
  });

  useEffect(() => {
    const fetchRecoveryCapitalData = async () => {
      try {
        if (!organizationId) throw new Error("Organization ID is required");
        setLoading(true);

        const calculatedData = await calculateOverviewMetrics(organizationId);

        setDomainData(calculatedData.domainData);
        setRcData(calculatedData.rcData);
        setRiskData(calculatedData.riskMetrics);
        setDomainDistributions(calculatedData.domainDistributions); // ADD THIS LINE

        setLoading(false);
      } catch (err) {
        console.error("Error fetching recovery capital data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchRecoveryCapitalData();
  }, [organizationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  if (!rcData) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <p className="text-white">No recovery capital data available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="h-10 w-1 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full" />
        <h2 className="text-2xl font-bold text-white">
          Recovery Capital Overview
        </h2>
      </div>

      <li className="flex items-start gap-3">
        <div className="mt-0.5 p-1 bg-purple-500/20 rounded-full">
          <Info size={16} className="text-purple-400" />
        </div>
        <div>
          <span className="text-white font-medium">
            Equal-weighted methodology
          </span>
          <p className="text-slate-400 text-sm">
            All statistics are calculated with per-person averaging, ensuring
            each resident contributes equally regardless of how many
            questionnaires they've completed. This prevents a single person from
            having too much influence on overal scores. If multiple
            questionnaires from the same resident are showing on a data point it
            will be explicitly stated.
          </p>
        </div>
      </li>

      {/* Dataset and Methodology */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-purple-400 mb-4">
              Dataset Composition
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="mt-0.5 p-1 bg-purple-500/20 rounded-full">
                  <Clipboard size={16} className="text-purple-400" />
                </div>
                <div>
                  <span className="text-white font-medium">
                    {rcData.assessmentCount} assessments
                  </span>
                  <p className="text-slate-400 text-sm">
                    From {rcData.totalUniqueIndividuals} unique individuals who
                    completed a questionnaire
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-0.5 p-1 bg-purple-500/20 rounded-full">
                  <Calendar size={16} className="text-purple-400" />
                </div>
                <div>
                  <span className="text-white font-medium">
                    Data collection period
                  </span>
                  <p className="text-slate-400 text-sm">
                    {rcData.dataCollectionStart} - {rcData.dataCollectionEnd}
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-0.5 p-1 bg-purple-500/20 rounded-full">
                  <Database size={16} className="text-purple-400" />
                </div>
                <div>
                  <span className="text-white font-medium">RC Total Mean</span>
                  <p className="text-slate-400 text-sm">
                    {rcData.rcMean.toFixed(1)} (SD = {rcData.rcSD.toFixed(1)})
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="flex-1">
            <h3 className="text-xl font-bold text-purple-400 mb-4">
              Domain Scores
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={domainData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.1)"
                  />
                  <XAxis type="number" domain={[0, 40]} stroke="#94a3b8" />
                  <YAxis
                    dataKey="name"
                    type="category"
                    stroke="#94a3b8"
                    width={120}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: "0.5rem",
                    }}
                    itemStyle={{ color: "#e2e8f0" }}
                    labelStyle={{ color: "#f8fafc", fontWeight: "bold" }}
                  />
                  <Bar dataKey="score" fill="#8b5cf6" radius={[0, 4, 4, 0]}>
                    {domainData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics - only keeping Domain Interrelationships and Risk Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-500/10 rounded-lg">
              <Activity className="text-emerald-400" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-white">
              Domain Interrelationships
              <Hint text="Pearson r measures correlation using: r = Σ[(Xi-X̄)(Yi-Ȳ)]/√[Σ(Xi-X̄)² × Σ(Yi-Ȳ)²] where Xi, Yi are individual domain scores per resident (averaged if multiple questionnaires). Values range from -1 (perfect negative) to 0 (no relation) to 1 (perfect positive). Each resident contributes equally regardless of questionnaire count." />
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Physical ↔ Individual</span>
              <span className="text-emerald-400 font-bold">
                {rcData.correlations.physicalToIndividual !== null
                  ? `r = ${rcData.correlations.physicalToIndividual.toFixed(2)}`
                  : "Calculating..."}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Social ↔ Individual</span>
              <span className="text-emerald-400 font-bold">
                {rcData.correlations.socialToIndividual !== null
                  ? `r = ${rcData.correlations.socialToIndividual.toFixed(2)}`
                  : "Calculating..."}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Individual ↔ Spiritual</span>
              <span className="text-emerald-400 font-bold">
                {rcData.correlations.individualToSpiritual !== null
                  ? `r = ${rcData.correlations.individualToSpiritual.toFixed(
                      2
                    )}`
                  : "Calculating..."}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">General ↔ Physical</span>
              <span className="text-emerald-400 font-bold">
                {rcData.correlations.generalToPhysical !== null
                  ? `r = ${rcData.correlations.generalToPhysical.toFixed(2)}`
                  : "Calculating..."}
              </span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-700/50">
            <p className="text-white text-sm">
              Individual Capital shows strongest correlations with other
              domains, suggesting it may be a critical intervention point.
            </p>
          </div>
        </div>

        {/* ─────────────── Risk Indicators ─────────────── */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <AlertTriangle className="text-amber-400" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-white">
              Risk & Protective Factors
              <Hint text="Based on alumni with known outcomes only. Dots show evidence confidence (green=high, yellow=moderate, gray=low). Numbers compare relapse rates between groups (e.g., '42% vs 11%' means 42% of people WITH prison history relapsed compared to 11% WITHOUT). Sample sizes shown when limited. Current residents are analyzed separately." />
            </h3>
          </div>

          {/* Data Quality Legend - New Addition */}
          <div className="mb-4 bg-slate-900/50 p-2 rounded-lg text-xs text-slate-300">
            <span className="font-medium text-amber-400 mr-1">
              Data quality:
            </span>
            Sample sizes are shown when limited (n&lt;10). Metrics with small
            samples are marked with a warning and should be interpreted with
            caution.
          </div>

          {/* Risk Factors Section */}
          <div className="mb-4">
            <h4 className="text-amber-400 font-medium mb-2 flex items-center">
              <ArrowUp className="mr-2 text-amber-400" size={16} />
              Risk Factors
            </h4>
            <div className="space-y-3 pl-2 border-l-2 border-amber-500/30">
              {/* For risk factors, we want to normalize to show OR > 1 (higher risk) */}
              <RiskRow
                label="Childhood Trauma"
                metric={normalizeMetric(riskData.trauma, false)}
                showSampleSizes={true}
              />
              <RiskRow
                label="Co‑Occurring Diagnosis"
                metric={normalizeMetric(riskData.cod, false)}
                showSampleSizes={true}
              />
              <RiskRow
                label="Homeless ≥ 1 Year"
                metric={normalizeMetric(riskData.housing, false)}
                showSampleSizes={true}
              />
              <RiskRow
                label="Spent ≥ 1 Year in Prison"
                metric={normalizeMetric(riskData.prison, false)}
                showSampleSizes={true}
              />
              <RiskRow
                label="Experiences Guilt/Shame"
                metric={normalizeMetric(riskData.guilt, false)}
                showSampleSizes={true}
              />
            </div>
          </div>

          {/* Protective Factors Section */}
          <div>
            <h4 className="text-emerald-400 font-medium mb-2 flex items-center">
              <ArrowDown className="mr-2 text-emerald-400" size={16} />
              Protective Factors
            </h4>
            <div className="space-y-3 pl-2 border-l-2 border-emerald-500/30">
              {/* For protective factors, we want to normalize to show OR < 1 (protective) */}
              <RiskRow
                label="Strong Purpose"
                metric={normalizeMetric(riskData.purpose, true)}
                showSampleSizes={true}
              />
              <RiskRow
                label="Two-Parent Home"
                metric={normalizeMetric(riskData.twoParentHome, true)}
                showSampleSizes={true}
              />
              {riskData.kidsRelationship && (
                <RiskRow
                  label="Strong Relationship with Kids"
                  metric={normalizeMetric(riskData.kidsRelationship, true)}
                  showSampleSizes={true}
                />
              )}
            </div>
          </div>
        </div>

        {/* Enhanced Domain Insights Card - Replace your existing one with this */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Brain className="text-purple-400" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-white">
              Domain Insights & Assessment Quality
              <Hint text="Analysis of domain performance, correlations, and questionnaire design effectiveness based on actual score distributions." />
            </h3>
          </div>

          {/* Domain Performance Overview */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <Heart className="text-purple-400 mt-1 flex-shrink-0" size={16} />
              <div>
                <div className="text-white font-medium">Spiritual Capital</div>
                <div className="text-slate-400 text-sm">
                  {domainData.length > 0 &&
                  domainData[0].name === "Spiritual Capital"
                    ? "Highest average domain score"
                    : domainData.length > 1 &&
                      domainData[1].name === "Spiritual Capital"
                    ? "Second highest domain score"
                    : "Important domain for recovery"}
                  {domainDistributions.find(
                    (d) => d.key === "spiritualCapitalScore"
                  ) && (
                    <span className="text-slate-500 ml-1">
                      (Mean:{" "}
                      {
                        domainDistributions.find(
                          (d) => d.key === "spiritualCapitalScore"
                        ).mean
                      }
                      , Range:{" "}
                      {
                        domainDistributions.find(
                          (d) => d.key === "spiritualCapitalScore"
                        ).min
                      }
                      -
                      {
                        domainDistributions.find(
                          (d) => d.key === "spiritualCapitalScore"
                        ).max
                      }
                      )
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Users className="text-blue-400 mt-1 flex-shrink-0" size={16} />
              <div>
                <div className="text-white font-medium">Social Capital</div>
                <div className="text-slate-400 text-sm">
                  {domainData.length > 0 &&
                  domainData[0].name === "Social Capital"
                    ? "Highest average domain score"
                    : domainData.length > 1 &&
                      domainData[1].name === "Social Capital"
                    ? "Second highest domain score"
                    : "Important domain for recovery"}
                  {domainDistributions.find(
                    (d) => d.key === "socialCapitalScore"
                  ) && (
                    <span className="text-slate-500 ml-1">
                      (Mean:{" "}
                      {
                        domainDistributions.find(
                          (d) => d.key === "socialCapitalScore"
                        ).mean
                      }
                      , Range:{" "}
                      {
                        domainDistributions.find(
                          (d) => d.key === "socialCapitalScore"
                        ).min
                      }
                      -
                      {
                        domainDistributions.find(
                          (d) => d.key === "socialCapitalScore"
                        ).max
                      }
                      )
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Brain
                className="text-emerald-400 mt-1 flex-shrink-0"
                size={16}
              />
              <div>
                <div className="text-white font-medium">Individual Capital</div>
                <div className="text-slate-400 text-sm">
                  Most connected to other domains (r = 0.53-0.56)
                  {domainDistributions.find(
                    (d) => d.key === "individualCapitalScore"
                  ) && (
                    <span className="text-slate-500 ml-1">
                      (Mean:{" "}
                      {
                        domainDistributions.find(
                          (d) => d.key === "individualCapitalScore"
                        ).mean
                      }
                      , Range:{" "}
                      {
                        domainDistributions.find(
                          (d) => d.key === "individualCapitalScore"
                        ).min
                      }
                      -
                      {
                        domainDistributions.find(
                          (d) => d.key === "individualCapitalScore"
                        ).max
                      }
                      )
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Activity
                className="text-orange-400 mt-1 flex-shrink-0"
                size={16}
              />
              <div>
                <div className="text-white font-medium">Physical Capital</div>
                <div className="text-slate-400 text-sm">
                  Excellent variance and scale utilization
                  {domainDistributions.find(
                    (d) => d.key === "physicalCapitalScore"
                  ) && (
                    <span className="text-slate-500 ml-1">
                      (Mean:{" "}
                      {
                        domainDistributions.find(
                          (d) => d.key === "physicalCapitalScore"
                        ).mean
                      }
                      , Range:{" "}
                      {
                        domainDistributions.find(
                          (d) => d.key === "physicalCapitalScore"
                        ).min
                      }
                      -
                      {
                        domainDistributions.find(
                          (d) => d.key === "physicalCapitalScore"
                        ).max
                      }
                      )
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <BookOpen
                className="text-pink-400 mt-1 flex-shrink-0"
                size={16}
              />
              <div>
                <div className="text-white font-medium">General Capital</div>
                <div className="text-slate-400 text-sm">
                  {domainData.length > 0 &&
                  domainData[domainData.length - 1].name === "General Capital"
                    ? "Area with most improvement opportunity"
                    : "Important area for development"}
                  {domainDistributions.find(
                    (d) => d.key === "generalCapitalScore"
                  ) && (
                    <span className="text-slate-500 ml-1">
                      (Mean:{" "}
                      {
                        domainDistributions.find(
                          (d) => d.key === "generalCapitalScore"
                        ).mean
                      }
                      , Range:{" "}
                      {
                        domainDistributions.find(
                          (d) => d.key === "generalCapitalScore"
                        ).min
                      }
                      -
                      {
                        domainDistributions.find(
                          (d) => d.key === "generalCapitalScore"
                        ).max
                      }
                      )
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced explanatory section */}
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <p className="text-slate-400 italic text-xs leading-relaxed">
              <span className="text-white font-medium">
                Domain Performance:
              </span>
              {domainDistributions.length > 0 && (
                <>
                  <br />
                  <br />
                  <strong className="text-emerald-400">
                    Spiritual Capital
                  </strong>{" "}
                  shows the{" "}
                  {domainData[0]?.name === "Spiritual Capital"
                    ? "highest"
                    : "strong"}{" "}
                  average scores (
                  {
                    domainDistributions.find(
                      (d) => d.key === "spiritualCapitalScore"
                    )?.mean
                  }
                  /40), consistent with a program emphasizing fellowship,
                  sponsorship, and spiritual growth.
                  {domainDistributions.find(
                    (d) => d.key === "spiritualCapitalScore"
                  )?.distributionQuality === "ceiling-bias"
                    ? " The slight ceiling bias suggests most residents develop strong spiritual foundations, though adding more advanced spiritual practice questions could differentiate highly engaged participants."
                    : " The distribution shows healthy variance across the spiritual development spectrum."}
                  <br />
                  <br />
                  <strong className="text-blue-400">Social Capital</strong>{" "}
                  ranks{" "}
                  {domainData[1]?.name === "Social Capital"
                    ? "second highest"
                    : "highly"}
                  (
                  {
                    domainDistributions.find(
                      (d) => d.key === "socialCapitalScore"
                    )?.mean
                  }
                  /40), reflecting effective peer support systems and community
                  building. The strong correlation with Individual Capital (r =
                  0.56) suggests that personal growth and social connection
                  develop together.
                  <br />
                  <br />
                  <strong className="text-emerald-400">
                    Individual Capital
                  </strong>{" "}
                  serves as the "hub domain" with the strongest correlations to
                  other areas (r = 0.53-0.56). At{" "}
                  {
                    domainDistributions.find(
                      (d) => d.key === "individualCapitalScore"
                    )?.mean
                  }
                  /40 average, it shows{" "}
                  {domainDistributions.find(
                    (d) => d.key === "individualCapitalScore"
                  )?.distributionQuality === "excellent"
                    ? "excellent variance"
                    : "good baseline development"}{" "}
                  across purpose, confidence, decision-making, and emotional
                  regulation. These interconnected skills create ripple effects:
                  gains in self-efficacy boost Physical Capital (motivation for
                  healthy habits) and Social Capital (trust, boundaries).
                  <br />
                  <br />
                  <strong className="text-orange-400">
                    Physical Capital
                  </strong>{" "}
                  demonstrates the most sound distribution (
                  {
                    domainDistributions.find(
                      (d) => d.key === "physicalCapitalScore"
                    )?.scaleUtilization
                  }
                  % scale utilization, mean{" "}
                  {
                    domainDistributions.find(
                      (d) => d.key === "physicalCapitalScore"
                    )?.mean
                  }
                  /40), effectively capturing the full spectrum from pain/sleep
                  issues to fitness and diet. This validates the questionnaire's
                  ability to detect meaningful individual differences in
                  physical recovery.
                  <br />
                  <br />
                  <strong className="text-pink-400">
                    General Capital
                  </strong>{" "}
                  shows the lowest average (
                  {
                    domainDistributions.find(
                      (d) => d.key === "generalCapitalScore"
                    )?.mean
                  }
                  /40) but excellent{" "}
                  {
                    domainDistributions.find(
                      (d) => d.key === "generalCapitalScore"
                    )?.scaleUtilization
                  }
                  % scale utilization. This domain uniquely captures structural
                  barriers and time-dependent milestones: employment stability,
                  legal resolution, transportation reliability, and 12-step
                  progression. Low scores reflect legitimate systemic challenges
                  rather than measurement flaws.
                  <br />
                  <br />
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RCPublicOverview;
